import type { CSSProperties, ReactNode } from "react";

import SectionCard from "@buildpy/ui/section-card";
import { cn } from "@/lib/utils";
import {
  addDays,
  chartDiffText,
  type ChartRange,
  type ChartRow,
  type PhaseChart as PhaseChartData,
} from "@buildpy/core/modules/project/phase-chart";
import { phaseScheduleText } from "@buildpy/core/modules/project/phase-schedule";
import { PROJECT_PHASE_STATUS_LABEL } from "@buildpy/core/modules/project/types";
import { PHASE_ACTUAL_BAR_CLASS, PHASE_STATUS_CLASS } from "@/modules/project/ui/phase-status-class";

// 하루 칸 최소 폭(px) — 좁은 화면에서는 차트 영역만 가로로 밀린다.
const DAY_PX = 14;
const LABEL_REM = 8;
// 차이 칸 — 「착수 지연 100일」(text-xs 약 88px)까지 한 줄에 든다.
const DIFF_REM = 6;

const pct = (n: number, range: ChartRange): string => `${(n / range.days) * 100}%`;

// 칸 자리는 CSS 변수로 넘기고 클래스(left-[var(--left)] w-[var(--w)])가 읽는다.
const SPAN_CLASS = "left-[var(--left)] w-[var(--w)]";

const todayLeft = (col: number, range: ChartRange): string => `calc(${pct(col + 0.5, range)} - 1px)`;

const diffClass = (row: ChartRow): string => {
  if (row.delayed) return "text-destructive";
  if (row.toneKind === "LATE") return "text-warning";
  return "text-muted-foreground";
};

interface TrackProps {
  range: ChartRange;
  holidayCols: readonly number[];
  todayCol: number | null;
  children?: ReactNode;
  className?: string;
}

// 한 줄의 시간 축 — 공휴일 띠·오늘 선을 줄마다 같은 자리에 그려 세로로 이어 보이게 한다.
const Track = ({ range, holidayCols, todayCol, children, className }: TrackProps) => (
  <div aria-hidden className={cn("relative", className)}>
    {holidayCols.map((col) => (
      <span
        key={col}
        className={cn("absolute inset-y-0 bg-muted", SPAN_CLASS)}
        style={{ "--left": pct(col, range), "--w": pct(1, range) } as CSSProperties}
      />
    ))}
    {children}
    {todayCol !== null && (
      <span
        className="absolute inset-y-0 left-[var(--left)] z-[1] w-0.5 bg-destructive"
        style={{ "--left": todayLeft(todayCol, range) } as CSSProperties}
      />
    )}
  </div>
);

interface Props {
  chart: PhaseChartData;
  // 공정 코드 → 그 회사에게 보이는 이름.
  names: ReadonlyMap<string, string>;
}

// 공정 차트 — 공정마다 계획(옅은 막대)·실제(굵은 선)·지연(붉은 꼬리), 오늘 선과 공휴일 띠. 라이브러리 없이 칸 비율로 그린다.
const PhaseChart = ({ chart, names }: Props) => {
  const { range, rows, todayCol, holidayCols } = chart;
  if (!range) {
    return chart.unscheduled > 0 ? (
      <p className="text-xs text-muted-foreground" data-testid="phase-chart-unscheduled">
        일정 미정 {chart.unscheduled}개 — 착공일과 공정 날짜가 정해지면 차트가 그려집니다.
      </p>
    ) : null;
  }
  const legend = (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      <li className="flex items-center gap-1.5">
        <span className="h-2.5 w-4 rounded-sm bg-accent ring-1 ring-inset ring-primary/30" />
        계획
      </li>
      <li className="flex items-center gap-1.5">
        <span className="h-1.5 w-4 rounded-sm bg-status-done" />
        완료
      </li>
      <li className="flex items-center gap-1.5">
        <span className="h-1.5 w-4 rounded-sm bg-primary" />
        진행
      </li>
      <li className="flex items-center gap-1.5">
        <span className="h-2.5 w-4 rounded-sm bg-destructive/15 ring-1 ring-inset ring-destructive/40" />
        지연
      </li>
      <li className="flex items-center gap-1.5">
        <span className="h-2.5 w-2 bg-muted ring-1 ring-inset ring-border" />
        공휴일
      </li>
      <li className="flex items-center gap-1.5">
        <span className="h-3 w-0.5 bg-destructive" />
        오늘
      </li>
    </ul>
  );

  return (
    <SectionCard
      actions={legend}
      className="min-w-0"
      data-testid="project-phase-chart"
      padding="sm"
      title="공정 차트">
      {chart.unscheduled > 0 && (
        <p className="mb-2 text-xs text-muted-foreground" data-testid="phase-chart-unscheduled">
          일정 미정 {chart.unscheduled}개 — 날짜가 없어 차트에서 뺐습니다.
        </p>
      )}
      <div className="overflow-x-auto" data-testid="phase-chart-scroll">
        <div
          className="grid min-w-[var(--min-w)] grid-cols-[var(--cols)] text-sm"
          style={
            {
              "--cols": `${LABEL_REM}rem minmax(0, 1fr) ${DIFF_REM}rem`,
              "--min-w": `calc(${LABEL_REM + DIFF_REM}rem + ${range.days * DAY_PX}px)`,
            } as CSSProperties
          }>
          <div className="sticky left-0 z-[2] flex h-10 items-end border-b border-border bg-card pb-1 text-xs text-muted-foreground">
            공정
          </div>
          <Track className="h-10 border-b border-border" holidayCols={holidayCols} range={range} todayCol={null}>
            {chart.ticks.map((tick) => (
              <span
                key={tick.col}
                className="absolute left-[var(--left)] top-0 whitespace-nowrap pl-1 text-xs tabular-nums text-muted-foreground"
                style={{ "--left": pct(tick.col, range) } as CSSProperties}>
                {tick.label}
              </span>
            ))}
            {todayCol !== null && (
              <>
                <span
                  className="absolute bottom-0 left-[var(--left)] top-5 w-0.5 bg-destructive"
                  style={{ "--left": todayLeft(todayCol, range) } as CSSProperties}
                />
                <span
                  className="absolute left-[var(--left)] top-5 z-[1] whitespace-nowrap bg-card px-1 text-xs font-semibold tabular-nums text-destructive"
                  data-testid="phase-chart-today"
                  style={{ "--left": `calc(${pct(todayCol + 0.5, range)} + 2px)` } as CSSProperties}>
                  오늘 {addDays(range.start, todayCol).slice(5)}
                </span>
              </>
            )}
          </Track>
          <div className="flex h-10 items-end justify-end border-b border-border pb-1 text-xs text-muted-foreground">
            차이
          </div>

          {rows.map((row) => {
            const name = names.get(row.phaseCode) ?? row.phaseCode;
            const judgedText = row.judged ? ` · ${phaseScheduleText(row.schedule)}` : "";
            const summary = `${name} 계획 ${row.startDate}~${row.endDate} · ${PROJECT_PHASE_STATUS_LABEL[row.status]}${judgedText}`;
            return (
              <div key={row.id} className="contents" data-testid={`phase-chart-row-${row.id}`}>
                {/* 폰(md 미만)은 상태를 위 줄로 올리고 이름을 두 줄까지 — 좁은 이름 칸에서 말줄임으로 잘리지 않게. */}
                <div className="sticky left-0 z-[2] flex min-h-9 min-w-0 flex-col justify-center border-b border-border bg-card py-1 pr-2 md:h-9 md:flex-row md:items-center md:justify-start md:gap-x-1.5 md:py-0">
                  <span className="sr-only">{summary}</span>
                  <span
                    aria-hidden
                    className={cn("shrink-0 whitespace-nowrap text-xs", PHASE_STATUS_CLASS[row.status])}>
                    {PROJECT_PHASE_STATUS_LABEL[row.status]}
                  </span>
                  <span aria-hidden className="line-clamp-2 min-w-0 break-words break-keep font-medium md:line-clamp-none md:truncate">
                    {name}
                  </span>
                </div>
                <Track className="min-h-9 border-b border-border md:h-9" holidayCols={holidayCols} range={range} todayCol={todayCol}>
                  <span
                    className={cn(
                      "absolute top-2 h-5 rounded-sm bg-accent ring-1 ring-inset",
                      SPAN_CLASS,
                      row.delayed ? "ring-destructive" : "ring-primary/30"
                    )}
                    data-testid={`phase-chart-plan-${row.id}`}
                    style={{ "--left": pct(row.plan.from, range), "--w": pct(row.plan.span, range) } as CSSProperties}
                    title={`${row.startDate} ~ ${row.endDate}`}
                  />
                  {row.overdue && (
                    <span
                      className={cn("absolute top-2 h-5 rounded-sm bg-destructive/15 ring-1 ring-inset ring-destructive/40", SPAN_CLASS)}
                      data-testid={`phase-chart-overdue-${row.id}`}
                      style={{ "--left": pct(row.overdue.from, range), "--w": pct(row.overdue.span, range) } as CSSProperties}
                    />
                  )}
                  {row.actual && (
                    <span
                      className={cn("absolute top-3.5 h-2 rounded-sm", SPAN_CLASS, PHASE_ACTUAL_BAR_CLASS[row.status])}
                      data-testid={`phase-chart-actual-${row.id}`}
                      style={{ "--left": pct(row.actual.from, range), "--w": pct(row.actual.span, range) } as CSSProperties}
                    />
                  )}
                </Track>
                <div
                  aria-hidden
                  className={cn(
                    "flex min-h-9 items-center justify-end border-b border-border text-xs font-medium tabular-nums md:h-9",
                    diffClass(row)
                  )}>
                  {row.judged ? chartDiffText(row.schedule) : ""}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </SectionCard>
  );
};

export default PhaseChart;
